import React from 'react';
import get from 'lodash.get';
import { defineMessages, FormattedMessage } from 'react-intl';
import { createSelector } from 'reselect';

import { emptyArray, emptyObject } from '../default-props/empty';

const multiPackageId = '**MULTIPACKAGE**';
export const STANDARD_PACKAGE_ID = '3';
export const ALL_PACKAGE_TYPES_ID = 'all';

const defaultGenericPackageId = `${STANDARD_PACKAGE_ID}-false-0`;
export const defaultGenericPackage = {
  packageId: defaultGenericPackageId,
  packageTypeId: `${STANDARD_PACKAGE_ID}`,
  name: 'Package',
};

const messages = defineMessages({
  multiPackage: {
    description: 'shipmentMegaContainer.multiPackage',
    defaultMessage: 'Multi Package',
    id: 'sawaEy',
  },
  package: {
    description: 'packageService.package',
    defaultMessage: 'Package',
    id: 'au/qLS',
  },
  packages: {
    description: 'packageService.packages',
    defaultMessage: 'Packages',
    id: 'ctHwnd',
  },
});

export const isGenericPackage = (packageId) => {
  const [packageTypeId] = packageId?.split('-') ?? emptyArray;
  return packageTypeId === STANDARD_PACKAGE_ID || packageTypeId === ALL_PACKAGE_TYPES_ID;
};

const findByIdSelector = createSelector(
  (packages) => packages,
  (_, packageId) => packageId,
  (packages, packageId) => {
    if (packageId === undefined) {
      return undefined;
    }
    if (packageId === defaultGenericPackageId) {
      return defaultGenericPackage;
    }
    return packages.find((p) => p.packageId === packageId);
  },
);

const selectedCarrierOrCustomFilter = (selectedService) => (pkg) => {
  return (!pkg.carrierId || pkg.carrierId === selectedService.carrierId) && !pkg.isHidden;
};

const customPackagesFilter = (pkg) => !pkg.carrierId;

class PackageServiceClass {
  buildCompositePackageId(packageTypeObject) {
    return `${packageTypeObject.packageTypeId}-${packageTypeObject.isCustom}-${packageTypeObject.carrierId}`;
  }

  parseInformationFromCompositePackageId(compositePackageId) {
    const packageTypeIdParts = (compositePackageId || '').split('-');

    if (packageTypeIdParts.length === 0) {
      return {
        packageTypeId: undefined,
        isCustomPackageType: undefined,
        carrierId: undefined,
      };
    }

    return {
      packageTypeId: packageTypeIdParts[0],
      isCustomPackageType: packageTypeIdParts[1] === 'true',
      carrierId: packageTypeIdParts[2],
    };
  }

  findBestGuessPackageByPackageTypeId(packageTypeId, packages, selectedPackageCarrierId = '') {
    return packages.find((findPackage) => {
      if (selectedPackageCarrierId) {
        return findPackage.carrierId === selectedPackageCarrierId && findPackage.packageTypeId === packageTypeId;
      }
      return findPackage.packageTypeId === packageTypeId;
    });
  }

  findById(packages, packageId) {
    return findByIdSelector(packages, packageId);
  }

  getStandardPackageId() {
    return STANDARD_PACKAGE_ID;
  }

  getAllPackageTypesId() {
    return ALL_PACKAGE_TYPES_ID;
  }

  getMultiPackageId() {
    return multiPackageId;
  }

  removeBadPackages(packages) {
    const filteredPackages = packages.filter((shippingPackage) => !!shippingPackage).filter((shippingPackage) => !shippingPackage.isHidden);

    return filteredPackages;
  }

  getPackages(carriers, services, packages, isReturn, selectedService, view = emptyObject) {
    const selectedCarrierId = get(
      services.find((service) => service.serviceId === selectedService),
      'carrierId',
      '',
    );
    const filteredPackages = this.removeBadPackages(packages);
    const mappedPackages = filteredPackages
      .filter((p) => p.carrierId === selectedCarrierId || p.isCustom)
      .map((p) => {
        const matchingCarrier = carriers.find((carrier) => carrier.carrierId === p.carrierId) || {};
        return {
          key: p.name,
          value: `${p.packageTypeId}-${p.isCustom}-${p.carrierId}`,
          packageTypeId: p.packageTypeId,
          isCustom: p.isCustom,
          dimensions: p.dimensions,
          isDefaultPackage: p.packageTypeId === STANDARD_PACKAGE_ID,
          carrierName: matchingCarrier.name,
        };
      });

    if (selectedCarrierId === '') {
      mappedPackages.push({
        key: 'Package',
        value: '3-false-0',
        packageTypeId: STANDARD_PACKAGE_ID.toString(),
        isCustom: false,
        isDefaultPackage: false,
        carrierName: <FormattedMessage {...messages.packages} />,
      });
    }

    if (!isReturn) {
      const servicesWithMultipackage = services.filter(
        (service) => service.features.supportsMultiPackage && service.carrierId === selectedCarrierId,
      );
      const carrierIdsThatSupportMultipackage = [...new Set(servicesWithMultipackage.map((service) => service.carrierId))];
      carrierIdsThatSupportMultipackage.forEach((carrierId) => {
        const matchingCarrier = carriers.find((carrier) => carrier.carrierId === carrierId) || {};
        const defaultPackageIndex = mappedPackages.findIndex((p) => p.packageTypeId === STANDARD_PACKAGE_ID.toString());
        mappedPackages.splice(defaultPackageIndex + 1, 0, {
          key: multiPackageId,
          value: multiPackageId,
          isCustom: false,
          isDefaultPackage: false,
          carrierName: matchingCarrier.name,
          disabled: get(view, 'shouldHideMultipackage', false),
        });
      });
    }

    return mappedPackages;
  }

  combineMultiPackages(packages) {
    if (!packages || packages.length === 0) {
      return undefined;
    }
    return packages.reduce((acc, cur) => {
      const weight = acc.weight ?? { value: 0 };
      const insuredValue = acc.insuredValue ?? { value: 0 };
      return {
        weight: {
          ...weight,
          value: weight.value + (cur?.weight?.value || 0),
        },
        insuredValue: {
          ...insuredValue,
          value: insuredValue.value + (cur?.insuredValue?.value || 0),
        },
      };
    });
  }

  getFilterFunction(selectedService) {
    return selectedService ? selectedCarrierOrCustomFilter(selectedService) : customPackagesFilter;
  }

  getCarrierIdFromPackageId(packageId) {
    return (packageId || '').split('-')[2];
  }

  compareCarrierIds(packageId1, packageId2) {
    return this.getCarrierIdFromPackageId(packageId1) === this.getCarrierIdFromPackageId(packageId2);
  }
}

export const PackageService = new PackageServiceClass();
